import { create } from 'zustand';
import {
  createCancellationAlert as createCancellationAlertData,
  getCancellationAlerts as getCancellationAlertsData,
  markAlertAsRead as markAlertAsReadData,
  type CancellationAlert,
  type CreateCancellationAlertParams,
  type GetCancellationAlertsFilters,
} from '@/services/data/cancellationAlerts';
import { logger } from '@/utils/logger';

interface CancellationAlertsState {
  // Alerts list
  alerts: CancellationAlert[];
  // Loading states
  loading: boolean;
  creating: boolean;
  markingAsRead: Record<string, boolean>;
  // Errors
  error: Error | null;
  // Filters used for current alerts list
  currentFilters: GetCancellationAlertsFilters | undefined;
  
  // Actions
  createAlert: (params: CreateCancellationAlertParams) => Promise<{ success: boolean; data?: CancellationAlert; error?: any }>;
  fetchAlerts: (filters?: GetCancellationAlertsFilters) => Promise<void>;
  markAsRead: (alertId: string) => Promise<{ success: boolean; data?: CancellationAlert; error?: any }>;
  getUnreadCount: () => number;
  clearAlerts: () => void;
}

export const useCancellationAlertsStore = create<CancellationAlertsState>((set, get) => ({
  // Initial state
  alerts: [],
  loading: false,
  creating: false,
  markingAsRead: {},
  error: null,
  currentFilters: undefined,

  // Create a new cancellation alert
  createAlert: async (params: CreateCancellationAlertParams) => {
    set({ creating: true, error: null });

    try {
      const result = await createCancellationAlertData(params);
      
      if (result.success && result.data) {
        // Add to alerts list if it matches current filters
        const state = get();
        const shouldInclude = !state.currentFilters || 
          (state.currentFilters.isRead === undefined || result.data.is_read === state.currentFilters.isRead) &&
          (!state.currentFilters.eventType || result.data.event_type === state.currentFilters.eventType);
        
        if (shouldInclude) {
          set((state) => ({
            alerts: [result.data!, ...state.alerts],
            creating: false,
            error: null,
          }));
        } else {
          set({ creating: false, error: null });
        }
        
        return { success: true, data: result.data };
      } else {
        set({ creating: false, error: result.error as Error || new Error('Failed to create alert') });
        logger.error('Error creating cancellation alert:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      set({ creating: false, error: error as Error });
      logger.error('Error creating cancellation alert:', error);
      return { success: false, error };
    }
  },

  // Fetch cancellation alerts
  fetchAlerts: async (filters?: GetCancellationAlertsFilters) => {
    set({ loading: true, error: null, currentFilters: filters });

    try {
      const result = await getCancellationAlertsData(filters);
      
      if (result.success && result.data) {
        set({
          alerts: result.data,
          loading: false,
          error: null,
        });
      } else {
        set({
          alerts: [],
          loading: false,
          error: result.error as Error || new Error('Failed to fetch alerts'),
        });
        logger.error('Error fetching cancellation alerts:', result.error);
      }
    } catch (error) {
      set({
        alerts: [],
        loading: false,
        error: error as Error,
      });
      logger.error('Error fetching cancellation alerts:', error);
    }
  },

  // Mark alert as read
  markAsRead: async (alertId: string) => {
    set((state) => ({
      markingAsRead: { ...state.markingAsRead, [alertId]: true },
      error: null,
    }));

    try {
      const result = await markAlertAsReadData(alertId);
      
      if (result.success && result.data) {
        // Update the alert in the list
        set((state) => ({
          alerts: state.alerts.map(alert => 
            alert.id === alertId ? result.data! : alert
          ),
          markingAsRead: { ...state.markingAsRead, [alertId]: false },
          error: null,
        }));
        
        return { success: true, data: result.data };
      } else {
        set((state) => ({
          markingAsRead: { ...state.markingAsRead, [alertId]: false },
          error: result.error as Error || new Error('Failed to mark alert as read'),
        }));
        logger.error('Error marking alert as read:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      set((state) => ({
        markingAsRead: { ...state.markingAsRead, [alertId]: false },
        error: error as Error,
      }));
      logger.error('Error marking alert as read:', error);
      return { success: false, error };
    }
  },

  // Get count of unread alerts
  getUnreadCount: () => {
    return get().alerts.filter(alert => !alert.is_read).length;
  },

  // Clear all alerts
  clearAlerts: () => {
    set({
      alerts: [],
      loading: false,
      creating: false,
      markingAsRead: {},
      error: null,
      currentFilters: undefined,
    });
  },
}));

